import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import ProBadge from "@/components/ProBadge";
import { Sparkles } from "lucide-react";

/**
 * Renders the user's Monthly Becoming Reflection.
 *
 * Backend returns either:
 *   - `{ sufficient_signal: false, message }` — encouraging empty state
 *   - `{ sufficient_signal: true, body, period, signal_counts }` — the reflection markdown
 */
export default function MonthlyBecomingReflection() {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await endpoints.monthlyBecomingReflection();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const heading = (
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--terracotta))]">
      <Sparkles size={12} />
      <span>Your Becoming Reflection</span>
      <ProBadge />
    </div>
  );

  if (state.loading) {
    return (
      <div className="p-8 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid="monthly-reflection-loading">
        {heading}
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Steeping your reflection…</p>
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="p-8 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid="monthly-reflection-error">
        {heading}
        <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">
          We couldn&rsquo;t load your reflection right now. It will be here on your next visit.
        </p>
      </div>
    );
  }

  const { sufficient_signal, message, body, period } = state.data;

  if (!sufficient_signal) {
    return (
      <div
        className="p-8 border hairline rounded-md bg-[hsl(var(--paper))]"
        data-testid="monthly-reflection-empty"
      >
        {heading}
        <p className="mt-4 font-display text-xl leading-snug text-[hsl(var(--fg))]/85 max-w-2xl">
          {message}
        </p>
      </div>
    );
  }

  return (
    <article
      data-testid="monthly-reflection"
      className="p-8 md:p-10 border hairline rounded-md bg-gradient-to-br from-[hsl(var(--sand))]/40 to-[hsl(var(--paper))]"
    >
      {heading}
      <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))] tabular-nums">{period}</div>
      <div
        className="mt-4 prose prose-neutral max-w-none text-[15px] leading-relaxed text-[hsl(var(--fg))]/90 whitespace-pre-wrap"
        data-testid="monthly-reflection-body"
      >
        {body}
      </div>
    </article>
  );
}
